import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { ExternalLinkIcon } from "shared/components";

export interface BGProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function GB({ assessmentLights, conversation }: BGProps) {
  const renderRequestFormalAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for Information on Planning Controls")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you need formal written advice for your builder or surveyor that a planning permit is not required, please lodged an application for information on planning controls through Council's Community Portal. Please note you will need to log in or create an account on the portal to utilise the service.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/building-and-business/planning/pre-application-advice-planning-permits"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Apply here")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckIfYouNeedABuildingPermit = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check if you need a building permit")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for works requiring authorisation under the Building Regulations 2018. Please visit Council's website 'do i need a permit' for further information on the types of work that need building permits. Please note Council does not issue building permits, this is arranged with private building surveyors",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/building-and-business/building"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Do i need a building permit?")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestCertificateOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request a Certificate of Title")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), agreements, caveats, and easements which can impact what you can and can't do on a property. A Certificate of Title can include this information and can be obtained online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.landata.online"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Landata")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderContactBusinessConciergeService = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Contact the Business Concierge Service")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Are you setting up a home-based or small business? You may need approvals from other areas of Council to operate the business. Please lodge an enquiry through Council's Business Concierge service and in the planning field quote your enquiry ID from MyLot",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/forms/business-help-desk-enquiry"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Business Permit Checklist")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDiscussWithPlanner = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>

        <p className="text-bodymd mb-2">
          {interpolate(
            "Have questions? {{emailLink}} to speak to one of our planners. When you contact us, please tell us your report ID:",
            {
              emailLink: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href="https://www.bendigo.vic.gov.au/building-and-business/planning/contact-planning"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("Contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  const renderRefineYourProposal = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Refine your proposal")}</h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Check your myLot report for the unanswered questions that will finalise the results of your advice.",
          )}
        </p>
      </div>
    );
  };

  const renderSeekPreApplicationAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Seek pre-application advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "For more substantial proposals to understand the constraints and considerations and if the application warrants Council's support, it is strongly encouraged to lodge an application for Pre-application Advice. This allows you to arrange meetings and receive written advice from Council. Please note application documents are required and don't forget to quote your enquiry ID from MyLot",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/building-and-business/planning/pre-application-advice-planning-permits"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Community Portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckYourRestrictionsOnTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check your restrictions on title")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), agreements, caveats, and easements which can impact what you can and can't do on a property. A Certificate of Title can include this information and can be obtained online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.landata.online"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Landata")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderApplyForInformationOnPlannerControls = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for information on planning controls")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you need formal written advice for your builder or surveyor that a planning permit is required, please lodged an application for information on planning controls through Council's Community Portal. Please note you will need to log in or create an account on the portal to utilise the service.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/building-and-business/planning/pre-application-advice-planning-permits"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Community Portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderLearnHowToApply = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Learn how to apply")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Like more information on the planning permit applications and how to apply? Have a look at Council's website which provides a number of handy resources and explanation on the process.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.bendigo.vic.gov.au/building-and-business/planning/planning-permit-process"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Learn how to apply")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestFormalAdvice()}
          {renderCheckIfYouNeedABuildingPermit()}
          {renderRequestCertificateOfTitle()}
          {renderContactBusinessConciergeService()}
          {renderDiscussWithPlanner()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderRefineYourProposal()}
          {renderSeekPreApplicationAdvice()}
          {renderRequestCertificateOfTitle()}
          {renderDiscussWithPlanner()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderCheckYourRestrictionsOnTitle()}
          {renderApplyForInformationOnPlannerControls()}
          {renderSeekPreApplicationAdvice()}
          {renderLearnHowToApply()}
          {renderDiscussWithPlanner()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussWithPlanner()}</>;
  }
}
